import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  TextField,
  LinearProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelEditIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  Map as MapIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, Link } from "react-router-dom";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";

const MedicalProfessionalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [medicalProfessional, setMedicalProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    mp_category: "",
    medical_license_number: "",
    medical_license_expiry: "",
    npi_number: "",
    drivers_license_number: "",
    drivers_license_expiry: "",
    vehicle_number: "",
    vehicle_type: "",
    vehicle_make: "",
    vehicle_model: "",
    license_plate: "",
    geographic_area: [],
  });

  // Categories and vehicle types
  const categories = [
    { value: "nurse", label: "Nurse" },
    { value: "doctor", label: "Doctor" },
    { value: "technician", label: "Technician" },
    { value: "phlebotomist", label: "Phlebotomist" },
    { value: "other", label: "Other" },
  ];

  const vehicleTypes = [
    { value: "sedan", label: "Sedan" },
    { value: "suv", label: "SUV" },
    { value: "van", label: "Van" },
    { value: "truck", label: "Truck" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    fetchMedicalProfessionalDetails();
  }, [id]);

  const fetchMedicalProfessionalDetails = async () => {
    setLoading(true);
    try {
      const response =
        await medicalProfessionalsService.getMedicalProfessionalById(id);
      if (response) {
        setMedicalProfessional(response);
        setFormData({
          mp_category: response.mp_category || "",
          medical_license_number: response.medical_license_number || "",
          medical_license_expiry: response.medical_license_expiry
            ? response.medical_license_expiry.split("T")[0]
            : "",
          npi_number: response.npi_number || "",
          drivers_license_number: response.drivers_license_number || "",
          drivers_license_expiry: response.drivers_license_expiry
            ? response.drivers_license_expiry.split("T")[0]
            : "",
          vehicle_number: response.vehicle_number || "",
          vehicle_type: response.vehicle_type || "",
          vehicle_make: response.vehicle_make || "",
          vehicle_model: response.vehicle_model || "",
          license_plate: response.license_plate || "",
          geographic_area: response.geographic_area || [],
        });
      } else {
        setError("Medical professional not found");
      }
    } catch (err) {
      console.error("Error fetching medical professional details:", err);
      setError("Failed to load medical professional details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = { ...formData };

      // Convert dates to proper format if needed
      if (updateData.medical_license_expiry) {
        updateData.medical_license_expiry = new Date(
          updateData.medical_license_expiry
        )
          .toISOString()
          .split("T")[0];
      }
      if (updateData.drivers_license_expiry) {
        updateData.drivers_license_expiry = new Date(
          updateData.drivers_license_expiry
        )
          .toISOString()
          .split("T")[0];
      }

      const response =
        await medicalProfessionalsService.updateMedicalProfessional(
          id,
          updateData
        );

      setMedicalProfessional(response);
      setEditing(false);
      setSuccess("Medical professional information updated successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error updating medical professional:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to update medical professional information"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // Reset form data to original medical professional data
    if (medicalProfessional) {
      setFormData({
        mp_category: medicalProfessional.mp_category || "",
        medical_license_number:
          medicalProfessional.medical_license_number || "",
        medical_license_expiry: medicalProfessional.medical_license_expiry
          ? medicalProfessional.medical_license_expiry.split("T")[0]
          : "",
        npi_number: medicalProfessional.npi_number || "",
        drivers_license_number:
          medicalProfessional.drivers_license_number || "",
        drivers_license_expiry: medicalProfessional.drivers_license_expiry
          ? medicalProfessional.drivers_license_expiry.split("T")[0]
          : "",
        vehicle_number: medicalProfessional.vehicle_number || "",
        vehicle_type: medicalProfessional.vehicle_type || "",
        vehicle_make: medicalProfessional.vehicle_make || "",
        vehicle_model: medicalProfessional.vehicle_model || "",
        license_plate: medicalProfessional.license_plate || "",
        geographic_area: medicalProfessional.geographic_area || [],
      });
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      nurse: "primary",
      doctor: "secondary",
      technician: "info",
      phlebotomist: "success",
      other: "default",
    };
    return colors[category?.toLowerCase()] || "default";
  };

  const formatCategory = (category) => {
    if (!category) return "N/A";
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString(); 
  };

  const getLocationTypeIcon = (type) => {
    switch (type) {
      case "home":
        return <HomeIcon />;
      case "office":
        return <BusinessIcon />;
      case "facility":
        return <BusinessIcon />;
      default:
        return <LocationIcon />;
    }
  };

  // Get starting point from geographic_area
  const getStartingPoint = () => {
    if (
      medicalProfessional?.geographic_area &&
      typeof medicalProfessional.geographic_area === "object"
    ) {
      return medicalProfessional.geographic_area.starting_point;
    }
    return null;
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !medicalProfessional) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/medical-professionals")}
        >
          Back to Medical Professionals
        </Button>
      </Box>
    );
  }

  if (!medicalProfessional) {
    return (
      <Box>
        <Alert severity="warning">Medical professional not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/medical-professionals")}
          sx={{ mt: 2 }}
        >
          Back to Medical Professionals
        </Button>
      </Box>
    );
  }

  const startingPoint = getStartingPoint();

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <Button
            component={Link}
            to="/medical-professionals"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Medical Professionals
          </Button>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {medicalProfessional.user?.full_name || "Medical Professional"}
            </Typography>
            <Chip
              label={formatCategory(medicalProfessional.mp_category)}
              color={getCategoryColor(medicalProfessional.mp_category)}
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>
        <Box>
          {!editing ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/medical-professionals/${id}/edit`)}
            >
              Edit Medical Professional
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ mr: 2 }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelEditIcon />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid  size={{ xs: 12, md: 8 }}>
          {/* User Information */}
          <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              User Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Full Name
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {medicalProfessional.user?.full_name || "N/A"}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {medicalProfessional.user?.email || "N/A"}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {medicalProfessional.user?.username || "N/A"}
                </Typography>
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={medicalProfessional.user?.status || "Active"}
                  color="success"
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Professional Information */}
          <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Professional Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Category
                </Typography>
                {editing ? (
                  <FormControl fullWidth size="small">
                    <Select
                      name="mp_category"
                      value={formData.mp_category}
                      onChange={handleInputChange}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {formatCategory(medicalProfessional.mp_category)}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Medical License Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="medical_license_number"
                    value={formData.medical_license_number}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {medicalProfessional.medical_license_number || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Medical License Expiry
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="medical_license_expiry"
                    type="date"
                    value={formData.medical_license_expiry}
                    onChange={handleInputChange}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {formatDate(medicalProfessional.medical_license_expiry)}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  NPI Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="npi_number"
                    value={formData.npi_number}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {medicalProfessional.npi_number || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Driver's License Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="drivers_license_number"
                    value={formData.drivers_license_number}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {medicalProfessional.drivers_license_number || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Driver's License Expiry
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="drivers_license_expiry"
                    type="date"
                    value={formData.drivers_license_expiry}
                    onChange={handleInputChange}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <Typography variant="body1" gutterBottom>
                    {formatDate(medicalProfessional.drivers_license_expiry)}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          {/* Starting Point Configuration - SIMPLIFIED */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Starting Point
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {startingPoint ? (
              <Grid container spacing={2}>
                <Grid  size={{ xs: 12 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    {getLocationTypeIcon(startingPoint.type)}
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      ml={1}
                    >
                      Name
                    </Typography>
                  </Box>
                  <Typography variant="body2" gutterBottom>
                    {startingPoint.name || "Not set"}
                  </Typography>
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {startingPoint.address || "Not set"}
                  </Typography>
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    label={startingPoint.type || "Not set"}
                    color="primary"
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                </Grid>
                <Grid  size={{ xs: 12 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<MapIcon />}
                    onClick={() => {
                      const url = `https://www.google.com/maps/search/${encodeURIComponent(
                        startingPoint.address
                      )}`;
                      window.open(url, "_blank");
                    }}
                  >
                    View on Maps
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Box textAlign="center" py={3}>
                <LocationIcon
                  sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No starting point configured
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  Configure a starting point to improve route optimization
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/medical-professionals/${id}/edit`)}
                >
                  Configure Starting Point
                </Button>
              </Box>
            )}
          </Paper>

          {/* Vehicle Information */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Vehicle Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Number
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="vehicle_number"
                    value={formData.vehicle_number}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {medicalProfessional.vehicle_number || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Type
                </Typography>
                {editing ? (
                  <FormControl fullWidth size="small">
                    <Select
                      name="vehicle_type"
                      value={formData.vehicle_type}
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      {vehicleTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {formatCategory(medicalProfessional.vehicle_type)}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Make
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="vehicle_make"
                    value={formData.vehicle_make}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {medicalProfessional.vehicle_make || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Vehicle Model
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="vehicle_model"
                    value={formData.vehicle_model}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {medicalProfessional.vehicle_model || "N/A"}
                  </Typography>
                )}
              </Grid>
              <Grid  size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  License Plate
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleInputChange}
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" gutterBottom>
                    {medicalProfessional.license_plate || "N/A"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* REMOVED: Geographic Area section */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MedicalProfessionalDetail;
